// A recording inside another page, as PCFL v3's Start here draws its "Watch the Introduction" step
// (RecordingBlock, compact): the shared player (src/product/Player.jsx: resume, the privacy hash,
// watched at the product's share) and the chapters under it, each playing from its minute. Where they
// are is saved as on the recording page (the database caps it and sets watched_at), never while staff
// view as a member. The full page, with Ask Adam and the minute links, is the recording's own page.
import { useEffect, useMemo, useState } from 'react'
import * as f from '../../lib/format.js'
import { useMember } from '../../app/member.jsx'
import { usePhone } from '../../lib/media.js'
import { Link } from '../../lib/router.jsx'
import { chapterTime } from '../../lib/timestamp.js'
import { useViewingAs } from '../../lib/viewAs.jsx'
import { useProductData } from '../../product/ProductData.jsx'
import { RecordingPlayer } from '../../product/Player.jsx'
import { loadRecordingContent, saveProgress } from '../../product/data.js'
import { isReleased, progressFor, progressPayload } from '../../product/model.js'
import { COPY } from './copy.js'
import { lessons, recordingHref, recordingName } from './model.js'

export function RecordingBlock({ product, recording }) {
  const { data, patch } = useProductData()
  const { me } = useMember()
  const phone = usePhone()
  const viewingAs = useViewingAs()
  const released = recording ? isReleased(data, recording.id) : false
  const [content, setContent] = useState({ id: null, row: null, failed: false })
  const [seek, setSeek] = useState(null)

  useEffect(() => {
    if (!recording || !released) return undefined
    let live = true
    loadRecordingContent(recording.id)
      .then(row => { if (live) setContent({ id: recording.id, row, failed: false }) })
      .catch(() => { if (live) setContent({ id: recording.id, row: null, failed: true }) })
    return () => { live = false }
  }, [recording?.id, released])

  const saved = recording ? progressFor(data, recording.id) : null
  const base = useMemo(() => ({ furthest: saved?.furthest_seconds || 0, watched: saved?.seconds_watched || 0 }), [recording?.id])
  if (!recording || !released) return <p className="od-empty">{COPY.notUp}</p>
  const row = content.id === recording.id ? content.row : null
  if (content.id === recording.id && (content.failed || !row)) return <p className="od-empty">{COPY.notUp}</p>
  if (!row) return <div className="od-block-loading" aria-busy="true" />

  const module = (data.modules || []).find(m => m.id === recording.module_id) || null
  const duration = row.duration_seconds || 0
  const chapters = row.chapters || []
  const watched = Boolean(saved?.watched_at)
  const pos = saved?.position_seconds || 0

  function onReport(report) {
    if (viewingAs) return
    saveProgress(me.person.id, recording.id, progressPayload(base, report, duration))
      .then(r => { if (r) patch(d => ({ ...d, progress: [...d.progress.filter(p => p.recording_id !== recording.id), r] })) })
      .catch(() => { /* the next report tries again */ })
  }

  const line = !watched && pos > 10 ? COPY.carryOnFrom(f.clock(pos)) : COPY.posterChapters(phone)
  return (
    <div className="od-block">
      <RecordingPlayer title={`${product.name}, ${recordingName(recording, module)}`} content={row} savedSeconds={pos} seek={seek}
        canSave={!viewingAs} onReport={onReport} watchedShare={Number(data.product?.watched_share) || 0.9} phone={phone}
        poster={{ recorded: '', line, pct: duration ? (watched ? 100 : ((saved?.furthest_seconds || 0) / duration) * 100) : 0, watched }} />
      {chapters.length > 0 && (
        <div className="od-block-chapters">
          <div className="od-panel-h">
            {COPY.chapters}
            <span className="od-small">{COPY.chaptersLine(lessons(chapters.length), duration ? f.duration(duration) : '')}</span>
          </div>
          <div className="od-chapters">
            {chapters.map(c => (
              <button key={c.start_seconds} type="button" className={seek?.seconds === c.start_seconds ? 'od-chap od-chap--on' : 'od-chap'}
                onClick={() => setSeek(s => ({ n: (s?.n || 0) + 1, seconds: c.start_seconds }))}>
                <span className="od-chap-no">{c.no ?? ''}</span>
                <span className="od-chap-title">{c.title}</span>
                <span className="od-chap-time">{chapterTime(c.start_seconds)}</span>
              </button>
            ))}
          </div>
          <Link className="od-textlink" to={recordingHref(product, data, recording)}>{COPY.openRecording(recordingName(recording, module))}</Link>
        </div>
      )}
    </div>
  )
}
